import { CommonModule } from '@angular/common';
import {
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { KendoModule } from '@shared/kendo/kendo.module';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { filter, map, Observable, startWith, Subscription, tap } from 'rxjs';
import {
  AddressType,
  CompanyAddress,
} from 'src/app/features/company-profile-data/models/company';

@Component({
  selector: 'app-address-form',
  standalone: true,
  imports: [
    CommonModule,
    KendoModule,
    DialogsModule,
    ReactiveFormsModule,
    TranslateModule,
    AutocompleteComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressFormComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
})
export class AddressFormComponent
  implements OnInit, ControlValueAccessor, OnDestroy
{
  @Input() label: string = 'label';
  @Input() addressType: AddressType | keyof typeof AddressType =
    AddressType.Secondary;

  public edit = false;
  public form: FormGroup;

  public addressLabel: string | undefined;
  public fullAddress: string = '';

  private onChange = (val: any) => {};
  private onTouch = () => {};
  private sub: Subscription | undefined;
  private _rawValue: any;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      country: null,
      state: null,
      city: null,
      postCode: null,
      address: null,
    });

    this.sub = this.form.get('country')?.valueChanges.subscribe(() => {
      this.form.get('state')?.reset(null, { onlySelf: true });
      this.form.get('city')?.reset(null, { onlySelf: true });
      this.form.get('address')?.reset(null, { onlySelf: true });
    });

    this.sub?.add(
      this.form.get('state')?.valueChanges.subscribe(() => {
        this.form.get('city')?.reset(null, { onlySelf: true });
        this.form.get('address')?.reset(null, { onlySelf: true });
      })
    );

    this.sub?.add(
      this.form.get('city')?.valueChanges.subscribe(() => {
        this.form.get('address')?.reset(null, { onlySelf: true });
      })
    );

    const labelsub = this.form.valueChanges
      .pipe(
        map((formValue) => {
          const fullAddress = this.concatAddress(formValue);
          if (fullAddress) {
            this.fullAddress = [
              this.concatAddress(formValue),
              formValue.address,
            ]
              .filter((e) => !!e)
              .join(', ');
          }
          return fullAddress ?? 'Address';
        })
      )
      .subscribe((label) => {
        this.addressLabel = label;
      });
    this.sub?.add(labelsub);

    this.form.valueChanges.subscribe((data) => this.onChange(this.toDTO(data)));
  }

  private toDTO(data: any) {
    const result: CompanyAddress = {
      addressTypeId: this.addressType,
      cityId: data.city?.id,
      id: this._rawValue?.id,
      map: this._rawValue?.map,
      address: data.address,
      postCode: data.city?.description,
    };
    if (result.postCode) {
      this.form.get('postCode')?.setValue(result.postCode);
    }
    return result;
  }

  private concatAddress(formValue: any) {
    const values = [
      formValue.country?.name,
      formValue.state?.name,
      formValue.city?.name,
      formValue.postCode,
    ].filter((entry) => !!entry);


    if (values.length == 0) {
      return null;
    }

    return values.join(', ');
  }

  writeValue(obj: any): void {
    const item = obj && obj.length > 0 ? obj[obj.length - 1] : obj;


    const startValue = {
      country: item?.map?.countryDTO,
      state: item?.map?.stateDTO,
      city: item?.map?.cityDTO,
      address: item?.address ?? null,
      postCode: item?.postCode ?? null,
    };
    this.fullAddress = [this.concatAddress(startValue), startValue.address]
      .filter((e) => !!e)
      .join(', ');

    this._rawValue = item;
    this.form.patchValue(startValue);
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  openEdit() {
    this.edit = true;
  }

  closeEdit() {
    this.edit = false;
  }
}
